import { 
  Search, 
  Bell, 
  PanelLeftClose, 
  PanelLeft, 
  Sun, 
  Moon, 
  LogOut, 
  User, 
  AlertTriangle, 
  Info, 
  Clock, 
  ShieldCheck, 
  HelpCircle, 
  ChevronDown, 
  ChevronRight, 
  Plus, 
  X, 
  CheckCircle2,
  Settings,
  CreditCard,
  Activity,
  BarChart2,
  Target,
  Users2,
  Sparkles,
  BookOpen,
  FolderKanban,
  Building2,
  Sliders,
  Check
} from 'lucide-react';
import { motion } from 'motion/react';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext.tsx';
import { View } from '../types.ts';
import { useNavigation } from '../context/NavigationContext';
import { useCompanies, useProjects, useProducts } from '@/api/queries';
import { useBranding } from '../hooks/useBranding.ts';

import { useClickOutside } from '../hooks/useClickOutside.ts';
import apiClient from '../api/client';

function NotificationMenu() {
  const { activeWorkspace } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([
    {
      id: 1,
      title: "Sprint Finalizada",
      description: "A sprint #12 foi concluída com sucesso. Verifique o relatório.",
      type: "success",
      isRead: false,
      createdAt: new Date().toISOString()
    },
    {
      id: 2,
      title: "Novo Comentário",
      description: "Você recebeu um novo comentário no projeto 'Alpha'.",
      type: "info",
      isRead: true,
      createdAt: new Date(Date.now() - 3600000).toISOString()
    }
  ]);
  const [loading, setLoading] = useState(false);

  const menuRef = useClickOutside(() => setShowNotifications(false));

  const handleToggleNotifications = () => {
    setShowNotifications(!showNotifications);
  };

  const safeNotifications = Array.isArray(notifications) ? notifications : [];
  const unreadCount = safeNotifications.filter(n => !n.isRead).length;

  const markAllAsRead = async () => {
    setNotifications(safeNotifications.map(n => ({ ...n, isRead: true })));
  };
  
  const markAsRead = async (id: number) => {
    setNotifications(safeNotifications.map(n => n.id === id ? { ...n, isRead: true } : n));
  };

  const getIconColor = (type: string) => {
    switch(type) {
      case 'success': return 'text-green-500 bg-green-50 border-green-100';
      case 'warning': return 'text-amber-500 bg-amber-50 border-amber-100';
      case 'error': return 'text-red-500 bg-red-50 border-red-100';
      default: return 'text-blue-500 bg-blue-50 border-blue-100';
    }
  };

  const getIcon = (type: string) => {
    switch(type) {
      case 'success': return CheckCircle2;
      case 'warning': return AlertTriangle;
      case 'error': return AlertTriangle;
      default: return Info;
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `Há ${diffMins} min`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `Há ${diffHrs} h`;
    return `Há ${Math.floor(diffHrs / 24)} d`;
  };

  return (
    <div className="relative" id="notifications-btn" ref={menuRef as any}>
      <button 
        onClick={handleToggleNotifications}
        className="relative w-9 h-9 rounded-xl flex items-center justify-center hover:bg-[#FAFAFA] transition-colors text-[#64748B] hover:text-[#111111]"
      >
        <Bell size={16} className={unreadCount > 0 ? "text-[#111111]" : ""} />
        {unreadCount > 0 && (
          <div className="absolute top-1.5 right-1.5 flex min-w-[14px] h-[14px] items-center justify-center rounded-full bg-[#111111] border-2 border-[#FFFFFF] px-[3px] text-[8px] font-bold text-white">
            {unreadCount}
          </div>
        )}
      </button>

      {showNotifications && (
        <div className="absolute right-0 mt-2.5 w-[320px] sm:w-[380px] bg-white border border-[#0F172A0F] rounded-[24px] p-4 shadow-[0_10px_40px_rgba(0,0,0,0.08)] z-50 flex flex-col animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="flex items-center justify-between pb-4 border-b border-[#0F172A0F] mb-2">
              <h3 className="text-sm font-bold text-[#111111] flex items-center gap-2">
                Notificações
                {unreadCount > 0 && (
                  <span className="bg-[#111111] text-white text-[10px] px-2 py-0.5 rounded-full font-bold">
                    {unreadCount} novas
                  </span>
                )}
              </h3>
              {unreadCount > 0 && (
                <button 
                  onClick={markAllAsRead}
                  className="text-[10px] font-bold text-[#64748B] hover:text-[#111111] transition-colors"
                >
                  Marcar todas como lidas
                </button>
              )}
            </div>

            <div className="flex flex-col max-h-[400px] overflow-y-auto pr-1 -mr-1 custom-scrollbar gap-1">
              {loading ? (
                 <div className="py-8 flex text-[#64748B] justify-center items-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#111111]"></div>
                 </div>
              ) : notifications.length > 0 ? (
                notifications.map(notification => {
                  const Icon = getIcon(notification.type);
                  return (
                    <div 
                      key={notification.id} 
                      className={`p-3 rounded-[16px] transition-all cursor-pointer border ${notification.isRead ? 'bg-transparent border-transparent hover:bg-[#FAFAFA]' : 'bg-[#FAFAFA] border-[#0F172A0F]'}`}
                      onClick={() => markAsRead(notification.id)}
                    >
                      <div className="flex gap-3">
                        <div className={`mt-0.5 shrink-0 flex items-center justify-center w-8 h-8 rounded-full border ${getIconColor(notification.type)}`}>
                          <Icon size={14} />
                        </div>
                        <div className="flex flex-col gap-1 flex-1">
                          <div className="flex justify-between items-start gap-2">
                            <h4 className={`text-xs font-bold ${notification.isRead ? 'text-[#111111]/80' : 'text-[#111111]'}`}>
                              {notification.title}
                            </h4>
                            <span className="text-[10px] text-[#64748B] flex items-center gap-1 shrink-0 whitespace-nowrap">
                              <Clock size={10} /> {formatTime(notification.createdAt)}
                            </span>
                          </div>
                          <p className={`text-[11px] leading-relaxed ${notification.isRead ? 'text-[#64748B]/80' : 'text-[#64748B]'}`}>
                            {notification.description}
                          </p>
                        </div>
                        {!notification.isRead && (
                          <div className="shrink-0 w-2 h-2 rounded-full bg-[#111111] self-center" />
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="py-8 flex flex-col items-center justify-center text-center gap-2">
                  <div className="w-12 h-12 rounded-full bg-[#FAFAFA] border border-[#0F172A0F] flex items-center justify-center text-[#64748B]">
                    <Bell size={20} />
                  </div>
                  <p className="text-xs text-[#64748B]">Nenhuma notificação no momento</p>
                </div>
              )}
            </div>
          </div>
      )}
    </div>
  );
}

function UserProfileMenu({ setCurrentView }: { setCurrentView?: (view: View) => void }) {
  const { user, dbUser, logout, activeWorkspace } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [status, setStatus] = useState<'online' | 'away' | 'busy' | 'focus'>('online');
  const menuRef = useClickOutside(() => setShowMenu(false));

  // Fallback active user if session token is missing or in mock mode
  const activeUser = user || dbUser || {
    displayName: 'Carlos Executive',
    email: 'admin@cyzor.com.br',
    photoURL: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80',
    isPlatformAdmin: true,
    role: 'Diretor Executivo / CEO'
  };

  const photoUrl = activeUser.photoURL || activeUser.avatarUrl || (activeUser as any).avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80';
  const userName = activeUser.displayName || activeUser.email?.split('@')[0] || 'Executivo Cyzor';
  const userRole = dbUser?.role || activeWorkspace?.role || activeUser.role || 'Diretor Executivo / CEO';

  const statusConfig = {
    online: { label: 'Online', color: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200/80' },
    away: { label: 'Ausente', color: 'bg-amber-500', badge: 'bg-amber-50 text-amber-700 border-amber-200/80' },
    busy: { label: 'Reunião', color: 'bg-rose-500', badge: 'bg-rose-50 text-rose-700 border-rose-200/80' },
    focus: { label: 'Modo Foco', color: 'bg-indigo-500', badge: 'bg-indigo-50 text-indigo-700 border-indigo-200/80' },
  };

  const handleNavigate = (view: View) => {
    setShowMenu(false);
    if (setCurrentView) setCurrentView(view);
  };

  return (
    <div className="relative" id="user-profile-btn" ref={menuRef as any}>
      {/* Executive Trigger Button */}
      <button 
        type="button"
        onClick={() => setShowMenu(!showMenu)}
        className="relative group p-0.5 rounded-full hover:ring-2 hover:ring-slate-300 transition-all cursor-pointer"
        title="Menu do Executivo"
      >
        <div className="w-9 h-9 rounded-full overflow-hidden border border-slate-200 shadow-2xs bg-slate-900 flex items-center justify-center">
          {photoUrl ? (
            <img 
              src={photoUrl} 
              alt={userName} 
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" 
              referrerPolicy="no-referrer" 
            />
          ) : (
            <div className="bg-[#0F172A] text-white font-display font-bold w-full h-full flex items-center justify-center text-xs uppercase tracking-wider">
              {userName.charAt(0)}
            </div>
          )}
        </div>
        {/* Status Indicator Dot (Unclipped) */}
        <span className={`absolute bottom-0.5 right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ring-1 ring-black/5 ${statusConfig[status].color}`} />
      </button>

      {/* Profile Popover Dropdown - Sleek Executive Theme */}
      {showMenu && (
        <div className="absolute right-0 mt-2.5 w-76 bg-white border border-slate-200/80 rounded-[20px] p-3.5 shadow-[0_12px_32px_rgba(15,23,42,0.12)] z-50 flex flex-col gap-3 animate-in fade-in slide-in-from-top-2 duration-150">
          
          {/* User Header Profile */}
          <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
            <div className="relative shrink-0 w-10 h-10 rounded-full">
              <div className="w-10 h-10 rounded-full overflow-hidden border border-slate-200 bg-slate-900">
                {photoUrl ? (
                  <img src={photoUrl} alt={userName} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <div className="w-full h-full bg-[#0F172A] text-white font-bold text-sm flex items-center justify-center">
                    {userName.charAt(0)}
                  </div>
                )}
              </div>
              <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white ${statusConfig[status].color}`} />
            </div>

            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-xs font-bold text-[#0F172A] truncate leading-snug">{userName}</span>
              <span className="text-[10px] text-slate-500 font-medium truncate">{activeUser.email}</span>
              <span className="text-[9px] font-bold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded mt-1 truncate max-w-[170px]">
                {userRole}
              </span>
            </div>
          </div>

          {/* Quick Status Selector */}
          <div className="flex items-center justify-between gap-1 bg-slate-50/80 border border-slate-200/60 p-1.5 rounded-xl">
            {(['online', 'busy', 'away', 'focus'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                className={`flex-1 py-1 px-1 rounded-lg text-[10px] font-bold flex items-center justify-center gap-1 transition-all cursor-pointer border ${
                  status === s
                    ? 'bg-white text-slate-900 border-slate-300 shadow-2xs font-bold'
                    : 'bg-transparent hover:bg-slate-200/50 text-slate-500 border-transparent'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${statusConfig[s].color}`} />
                <span className="truncate">{statusConfig[s].label}</span>
              </button>
            ))}
          </div>

          {/* Navigation Links */}
          <div className="flex flex-col gap-0.5">
            <button
              type="button"
              onClick={() => handleNavigate('perfil')}
              className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold text-slate-700 hover:text-[#0F172A] hover:bg-slate-100/80 transition-all cursor-pointer group"
            >
              <div className="flex items-center gap-2">
                <User size={14} className="text-slate-500 group-hover:text-slate-800" />
                <span>Perfil Executivo</span>
              </div>
              <ChevronRight size={13} className="text-slate-400 group-hover:translate-x-0.5 transition-transform" />
            </button>

            <button
              type="button"
              onClick={() => handleNavigate('configuracoes')}
              className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold text-slate-700 hover:text-[#0F172A] hover:bg-slate-100/80 transition-all cursor-pointer group"
            >
              <div className="flex items-center gap-2">
                <Settings size={14} className="text-slate-500 group-hover:text-slate-800" />
                <span>Configurações</span>
              </div>
              <ChevronRight size={13} className="text-slate-400 group-hover:translate-x-0.5 transition-transform" />
            </button>

            <button
              type="button"
              onClick={() => handleNavigate('equipe')}
              className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold text-slate-700 hover:text-[#0F172A] hover:bg-slate-100/80 transition-all cursor-pointer group"
            >
              <div className="flex items-center gap-2">
                <Users2 size={14} className="text-slate-500 group-hover:text-slate-800" />
                <span>Gestão de Equipe</span>
              </div>
              <ChevronRight size={13} className="text-slate-400 group-hover:translate-x-0.5 transition-transform" />
            </button>

            <button
              type="button"
              onClick={() => handleNavigate('pulse')}
              className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold text-slate-700 hover:text-[#0F172A] hover:bg-slate-100/80 transition-all cursor-pointer group"
            >
              <div className="flex items-center gap-2">
                <Activity size={14} className="text-slate-500 group-hover:text-slate-800" />
                <span>Pulse Feed & Métricas</span>
              </div>
              <ChevronRight size={13} className="text-slate-400 group-hover:translate-x-0.5 transition-transform" />
            </button>

            {(dbUser?.isPlatformAdmin || activeUser?.isPlatformAdmin) && (
              <button
                type="button"
                onClick={() => handleNavigate('admin' as any)}
                className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-bold text-slate-900 bg-slate-100 hover:bg-slate-200/70 transition-all cursor-pointer group mt-1"
              >
                <div className="flex items-center gap-2">
                  <ShieldCheck size={14} className="text-indigo-600" />
                  <span>Painel Admin Global</span>
                </div>
                <ChevronRight size={13} className="text-slate-400 group-hover:translate-x-0.5 transition-transform" />
              </button>
            )}
          </div>

          {/* Copilot & Logout Actions */}
          <div className="pt-2 border-t border-slate-100 flex flex-col gap-1">
            <button
              type="button"
              onClick={() => {
                setShowMenu(false);
                window.dispatchEvent(new Event('open-cyzor-chat'));
              }}
              className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold text-slate-700 hover:bg-indigo-50/60 hover:text-indigo-700 transition-all cursor-pointer group"
            >
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-indigo-600" />
                <span>Assistente Copilot</span>
              </div>
              <span className="text-[9px] font-mono font-bold bg-indigo-50 text-indigo-700 border border-indigo-200/60 px-1.5 py-0.2 rounded">IA</span>
            </button>

            <button 
              type="button"
              onClick={() => {
                setShowMenu(false);
                logout();
              }}
              className="w-full flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold text-rose-600 hover:bg-rose-50 transition-all cursor-pointer mt-0.5"
            >
              <LogOut size={13} />
              <span>Sair da Conta</span>
            </button>
          </div>

        </div>
      )}
    </div>
  );
}

function WorkspaceSelector() {
  const { activeWorkspace, setActiveWorkspace, workspaces, setWorkspaces, selectWorkspace, isSwitchingWorkspace, setIsCreateWorkspaceModalOpen } = useAuth() as any;
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useClickOutside(() => setShowMenu(false));

  const loadWorkspaces = async () => {
    try {
      const { data } = await apiClient.get('/api/workspaces');
      const list = Array.isArray(data) ? data : [];
      if (list.length > 0) {
        setWorkspaces((prev: any[]) => {
          if (JSON.stringify(prev) === JSON.stringify(list)) return prev;
          return list;
        });
        if (!activeWorkspace) {
          setActiveWorkspace(list[0]);
        }
      }
    } catch (err) {
      console.warn("Failed to load real workspaces:", err);
    }
  };

  useEffect(() => {
    loadWorkspaces();
  }, []);

  const handleSelectWorkspace = async (ws: any) => {
    try {
      await apiClient.put('/api/user/active-workspace', { workspaceId: ws.id });
      if (selectWorkspace) {
        await selectWorkspace(ws);
      } else {
        setActiveWorkspace(ws);
      }
      setShowMenu(false);
      window.location.reload();
    } catch (err) {
      console.warn("Failed to switch active workspace:", err);
    }
  };

  return (
    <div className="relative" ref={menuRef as any}>
      <button 
        onClick={() => {
          const next = !showMenu;
          setShowMenu(next);
          if (next) loadWorkspaces();
        }}
        className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-[#FAFAFA] transition-colors group"
      >
        <div className="w-6 h-6 rounded-md bg-[#111111] flex items-center justify-center text-[10px] font-display font-black text-white">
          {activeWorkspace?.name?.charAt(0) || workspaces[0]?.name?.charAt(0) || 'W'}
        </div>
        <span className="text-xs font-display font-bold text-[#111111] max-w-[120px] truncate">
          {activeWorkspace?.name || workspaces[0]?.name || 'Workspace Principal'}
        </span>
        <ChevronDown size={14} className={`text-[#64748B] transition-transform duration-200 ${showMenu ? 'rotate-180' : ''}`} />
      </button>

      {showMenu && (
        <div className="absolute left-0 mt-2 w-64 bg-white border border-[#0F172A0F] rounded-[20px] p-2 shadow-[0_10px_30px_rgba(0,0,0,0.08)] z-50 flex flex-col gap-1 animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="px-3 py-2 border-b border-[#0F172A05] mb-1 flex items-center justify-between">
              <span className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest">Meus Workspaces</span>
              <button 
                onClick={() => {
                  setIsCreateWorkspaceModalOpen(true);
                  setShowMenu(false);
                }}
                className="p-1 hover:bg-[#FAFAFA] rounded-lg text-[#111111] transition-colors"
                title="Novo Workspace"
              >
                <Plus size={14} />
              </button>
            </div>
            <div className="max-h-[300px] overflow-y-auto custom-scrollbar flex flex-col gap-1 pr-1">
              {workspaces.map((workspace: any) => (
                <button
                  key={workspace.id}
                  onClick={() => handleSelectWorkspace(workspace)}
                  className={`w-full flex items-center gap-3 p-2 rounded-xl transition-all ${
                    (activeWorkspace?.id === workspace.id || (!activeWorkspace && workspaces[0]?.id === workspace.id))
                      ? 'bg-[#111111]/5 text-[#111111] font-bold' 
                      : 'text-[#64748B] hover:bg-[#FAFAFA] hover:text-[#111111]'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold shadow-sm ${
                    (activeWorkspace?.id === workspace.id || (!activeWorkspace && workspaces[0]?.id === workspace.id)) ? 'bg-[#111111] text-white' : 'bg-[#F1F5F9] text-[#64748B]'
                  }`}>
                    {workspace.name.charAt(0)}
                  </div>
                  <div className="flex flex-col items-start overflow-hidden">
                    <span className="text-xs truncate w-full text-left">{workspace.name}</span>
                    <span className="text-[9px] font-medium opacity-60 uppercase tracking-tighter">
                      {workspace.role || 'Membro'}
                    </span>
                  </div>
                  {(activeWorkspace?.id === workspace.id || (!activeWorkspace && workspaces[0]?.id === workspace.id)) && (
                    <div className="ml-auto w-1.5 h-1.5 rounded-full bg-[#111111]" />
                  )}
                </button>
              ))}
            </div>
            
            <div className="mt-1 pt-1 border-t border-[#0F172A05]">
              <button
                onClick={() => {
                  setIsCreateWorkspaceModalOpen(true);
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-3 p-2 rounded-xl text-[#111111] font-bold hover:bg-[#FAFAFA] transition-all text-xs"
              >
                <div className="w-8 h-8 rounded-lg bg-[#FAFAFA] border border-dashed border-[#0F172A20] flex items-center justify-center text-[#64748B]">
                  <Plus size={16} />
                </div>
                Criar novo Workspace
              </button>
            </div>
          </div>
      )}
    </div>
  );
}

function BreadcrumbNavigator({ setCurrentView }: { setCurrentView?: (view: View) => void }) {
  const { globalFilters, setGlobalFilters } = useNavigation();
  const { activeWorkspace } = useAuth();
  const workspaceId = activeWorkspace?.id || '';
  const { data: companiesData } = useCompanies(workspaceId);
  const { data: projectsData } = useProjects(workspaceId);
  const { data: productsData } = useProducts(workspaceId);

  const parts = [{ label: "Workspace", view: "dashboard" as View }];
  if (globalFilters.companyId) {
    const c = companiesData?.find(x => x.id.toString() === globalFilters.companyId?.toString());
    if (c) parts.push({ label: c.name, view: "empresas" as View });
  }
  if (globalFilters.projectId) {
    const p = projectsData?.find(x => x.id.toString() === globalFilters.projectId?.toString());
    if (p) parts.push({ label: p.name, view: "projetos" as View });
  }
  if (globalFilters.productId) {
    const p = productsData?.find(x => x.id.toString() === globalFilters.productId?.toString());
    if (p) parts.push({ label: p.name, view: "produtos" as View });
  }

  const handleBreadcrumbNavigation = (view: View) => {
    if (!setCurrentView) return;

    if (view === 'projetos') {
      setGlobalFilters(prev => ({ ...prev, projectId: undefined }));
    }
    if (view === 'produtos') {
      setGlobalFilters(prev => ({ ...prev, productId: undefined }));
    }
    if (view === 'clientes') {
      setGlobalFilters(prev => ({ ...prev, clientId: undefined }));
    }
    if (view === 'empresas') {
      setGlobalFilters(prev => ({ ...prev, companyId: undefined }));
    }

    setCurrentView(view);
  };

  return (
    <div className="flex items-center gap-2 text-[12px] font-bold text-[#111111]">
      {parts.map((p, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <span
            className="cursor-pointer hover:text-[#000000] opacity-70 hover:opacity-100 transition-opacity"
            onClick={() => handleBreadcrumbNavigation(p.view)}
          >
            {p.label}
          </span>
          {idx < parts.length - 1 && <ChevronRight size={14} className="opacity-40" />}
        </div>
      ))}
    </div>
  );
}

export default function Topbar({ isSidebarCollapsed, toggleSidebar, setCurrentView, currentView }: { isSidebarCollapsed: boolean, toggleSidebar: () => void, setCurrentView?: (view: View) => void, currentView?: View | 'admin' }) {
  const [isDark, setIsDark] = useState(false);
  const [time, setTime] = useState(new Date());
  const { activeWorkspace } = useAuth();
  const { appName } = useBranding();
  const [isOnline, setIsOnline] = useState(true);

  const sectionLabelMap: Record<string, string> = {
    dashboard: 'Dashboard',
    roadmap: 'Estratégia',
    empresas: 'Empresas',
    clientes: 'Clientes',
    produtos: 'Produtos',
    projetos: 'Projetos',
    ideias: 'Ideias',
    financeiro: 'Financeiro',
    documentacao: 'Documentação',
    ia: 'IA',
    agenda: 'Agenda',
    keep: 'Keep',
    'flow-builder': 'Flow Builder',
    configuracoes: 'Configurações',
    equipe: 'Equipe',
    admin: 'Admin'
  };

  const sectionLabel = currentView && sectionLabelMap[currentView] ? sectionLabelMap[currentView] : 'Workspace';

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const isDarkStored = localStorage.getItem('theme') === 'dark';
    setIsDark(isDarkStored);
    if (isDarkStored) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  const toggleTheme = () => {
    const newDark = !isDark;
    setIsDark(newDark);
    localStorage.setItem('theme', newDark ? 'dark' : 'light');

    if (newDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  return (
    <motion.header
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className={`fixed top-0 right-0 h-16 lg:h-20 bg-white/80 backdrop-blur-xl border-b border-white shadow-[0_10px_30px_rgba(0,0,0,0.03)] flex items-center justify-between px-4 sm:px-6 md:px-10 z-40 transition-[left,padding,margin] duration-300 left-0 ${isSidebarCollapsed ? 'lg:left-[89px]' : 'lg:left-[290px]'}`}
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-1/4 w-32 h-32 bg-indigo-50/20 rounded-full blur-3xl" />
      </div>
      <div className="flex items-center gap-4 lg:gap-8 min-w-0 flex-1">
        <motion.button
          whileHover={{ scale: 1.02, y: -1 }}
          whileTap={{ scale: 0.98 }}
          onClick={toggleSidebar}
          className="text-[#64748B] hover:text-[#111111] transition-colors duration-200 flex items-center justify-center w-10 h-10 rounded-xl hover:bg-[#FAFAFA] flex-shrink-0 group"
        >
          {isSidebarCollapsed ? <PanelLeft size={18} className="group-hover:scale-110 transition-transform duration-200" /> : <PanelLeftClose size={18} className="group-hover:scale-110 transition-transform duration-200" />}
        </motion.button>

        <div className="flex min-w-0 items-center gap-2 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className={`h-2 w-2 rounded-full shrink-0 ${isOnline ? 'bg-emerald-500' : 'bg-amber-500'}`} />
            <h2 className="text-sm sm:text-base font-display font-black text-[#111111] truncate">{sectionLabel}</h2>
          </div>
          
          <div className="hidden lg:flex items-center gap-2 text-[12px] font-medium text-[#64748B] pl-4 border-l border-[#0F172A0A]">
            <BreadcrumbNavigator setCurrentView={setCurrentView} />
          </div>
        </div>

        <div className="hidden lg:flex flex-1 max-w-[320px] ml-auto mr-6">
          <button
            onClick={() => window.dispatchEvent(new Event('open-command-palette'))}
            className="w-full bg-[#FAFAFA] hover:bg-[#F1F5F9] focus:bg-white border border-transparent focus:border-[#0F172A15] rounded-xl py-2 pl-9 pr-3 text-xs text-left transition-all duration-200 text-[#111111] font-medium relative flex items-center justify-between text-[#64748B]/70"
          >
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#64748B] opacity-50" size={14} />
            <span>Pesquisar...</span>
            <kbd className="hidden sm:inline-block text-[9px] font-mono bg-white border border-[#0F172A15] px-1.5 py-0.5 rounded text-[#64748B]">Ctrl+K</kbd>
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
        <div className="hidden xl:flex items-center gap-6 pr-6 border-r border-[#0F172A0A]">
          <div className="flex flex-col items-end gap-0.5">
            <span className="text-xs font-display font-black text-[#111111]">
              {(activeWorkspace?.settings?.besScore || 0).toLocaleString('pt-BR')} BES
            </span>
            <span className="text-[9px] font-bold text-[#64748B] uppercase tracking-wider">
              Score Global
            </span>
          </div>
          <div className="flex flex-col items-end gap-0.5">
            <span className="text-xs font-display font-black text-[#111111]">
              {time.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </span>
            <span className="text-[9px] font-bold text-[#64748B] uppercase tracking-wider">
              {time.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).toUpperCase()}
            </span>
          </div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <WorkspaceSelector />
        </div>

        <div className="flex items-center gap-1 sm:gap-1.5 pl-1 sm:pl-2">
          {/* Mobile Search Button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => window.dispatchEvent(new Event('open-command-palette'))}
            className="lg:hidden w-9 h-9 rounded-xl flex items-center justify-center hover:bg-[#FAFAFA] transition-colors text-[#64748B] hover:text-[#111111]"
            title="Pesquisar (Ctrl+K)"
          >
            <Search size={16} />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleTheme}
            className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-[#FAFAFA] transition-colors text-[#64748B] hover:text-[#111111]"
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => window.dispatchEvent(new Event('restart-tour'))}
            className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-[#FAFAFA] transition-colors text-[#64748B] hover:text-[#111111]"
            title="Fazer Tour"
          >
            <HelpCircle size={16} />
          </motion.button>
          
          <NotificationMenu />
          <div className="ml-1">
            <UserProfileMenu setCurrentView={setCurrentView} />
          </div>
        </div>
      </div>
    </motion.header>
  );
}
